import { useContext, useEffect } from 'react';
import { OptimizelyContext, OptimizelyDecideOption } from '@optimizely/react-sdk';
import { getOrCreateUserId } from '../provider/user-id';
import { EXPERIMENT_FLAG_KEYS } from '../experiment-registry';
import { Condition, evaluateCondition } from '../conditions';

interface PageEventRule {
	eventKey: string;
	conditions: Condition;
}

export function useExperimentPageTracking() {
	const { optimizely } = useContext(OptimizelyContext);

	useEffect(() => {
		if (!optimizely) return;

		const controller = new AbortController();

		optimizely.onReady().then(async () => {
			if (controller.signal.aborted) return;

			const userId = getOrCreateUserId() ?? undefined;
			const fired = new Set<string>();

			for (const flagKey of EXPERIMENT_FLAG_KEYS) {
				if (controller.signal.aborted) return;

				const decision = optimizely.decide(
					flagKey,
					[OptimizelyDecideOption.DISABLE_DECISION_EVENT],
					userId
				);
				const variable = (decision?.variables?.['page_events'] ?? {}) as {
					rules?: PageEventRule[];
				};

				for (const rule of variable.rules ?? []) {
					if (!rule.conditions || fired.has(rule.eventKey) || controller.signal.aborted) continue;

					const matches = await evaluateCondition(rule.conditions, controller.signal);
					if (matches) {
						optimizely.track(rule.eventKey, userId);
						fired.add(rule.eventKey);
					}
				}
			}
		});

		return () => {
			controller.abort();
		};
	}, [optimizely]);
}
